import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useHubStore } from '@renderer/stores/hubStore'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'

/**
 * Member-side half of the hub hand-off. When the booth arrives at a member
 * project with a pending auto-start ticket, call the Welcome's own
 * `startSession` once — but only after the member's config AND catalogue are
 * the ones in the stores (`config._id` and `sessionState.projectId` both equal
 * the route's project). Until then the member Welcome renders its loading
 * state (`pending`) so there is no flash of a second Welcome.
 *
 * The camera gate, credit checks, session id and telemetry all stay inside
 * `startSession`; this hook merely replaces the tap. If the gate fails the
 * ticket is already consumed and the normal tap UI shows (documented).
 *
 * It is also where this booth's CAMERA BINDING for the member lands. The
 * binding is local (`settingsStore.cameraSlotByProject`) because it describes
 * this machine's hardware, not the hub — booth A's second camera is not booth
 * B's. It cannot be applied before the start either: `startSession` resets
 * `userPreferences.selectedCamera` to 'primary', so an earlier write is
 * silently undone. Every page that captures or previews reads that preference
 * as its `cameraKey`, so nothing downstream needs to know about hubs.
 */
export function useHubAutoStart(startSession: () => void | Promise<void>): { pending: boolean } {
  const { projectId: routeParam } = useParams()
  const pendingAutoStart = useHubStore((s) => s.pendingAutoStart)
  const memberProjectId = useHubStore((s) => s.memberProjectId)
  const configId = useConfigStore((s) => s.config?._id)
  const sessionProjectId = useSessionStateStore((s) => s.projectId)

  const startRef = useRef(startSession)
  startRef.current = startSession

  const isOurMember = Boolean(routeParam && memberProjectId && memberProjectId === routeParam)
  const ready = isOurMember && configId === memberProjectId && sessionProjectId === memberProjectId

  useEffect(() => {
    if (!pendingAutoStart || !ready) return
    // consumeAutoStart is atomic: a re-render racing this effect gets false.
    if (!useHubStore.getState().consumeAutoStart()) return
    const memberId = useHubStore.getState().memberProjectId
    const slot = memberId
      ? useSettingsStore.getState().cameraSlotByProject?.[memberId]
      : undefined
    void Promise.resolve(startRef.current()).then(() => {
      // After the start, never before: the session reset inside `startSession`
      // hard-writes 'primary' and would silently undo an earlier write.
      if (slot) {
        useSessionStateStore.getState().setSessionState({
          userPreferences: { selectedCamera: slot }
        })
      }
    })
  }, [pendingAutoStart, ready])

  return { pending: pendingAutoStart && isOurMember }
}

/**
 * The camera this booth is bound to for the mode on screen, or undefined when
 * the booth is not on a hub member or no binding was set here.
 *
 * Its presence is what suppresses the `camera` chooser page: that page exists so
 * a GUEST can pick between two cameras, and here the operator has already
 * chosen on this booth. Asking the guest anyway would let them override the
 * binding the operator set in Camera settings.
 */
export function useHubCameraSlot(): 'primary' | 'secondary' | undefined {
  const { projectId: routeParam } = useParams()
  const memberProjectId = useHubStore((s) => s.memberProjectId)
  const byProject = useSettingsStore((s) => s.cameraSlotByProject)
  if (!routeParam || memberProjectId !== routeParam) return undefined
  return byProject?.[routeParam]
}
